import React from 'react';
import { usePermission } from '@/features/settings/hooks';
import type { SettingsEntry } from '../registry';
import { SettingControlRow } from './setting-control-row';
import { SettingCustomRow } from './setting-custom-row';
import { SettingPermissionRow } from './setting-permission-row';

interface RegistryRowProps {
  entry: SettingsEntry;
  isLast?: boolean;
}

/**
 * Dispatches a registry entry to its concrete row and resolves the
 * `dependsOnPermission` gate. Unlike platform/flag gating (which *hides* an
 * entry at the section level), a denied dependency *disables* the row — the
 * mechanism lives here because it needs the permission hook. `usePermission` is
 * called unconditionally (Rules of Hooks); its result only matters when the
 * entry actually declares a dependency.
 */
export function RegistryRow({ entry, isLast = false }: RegistryRowProps) {
  const dependency = usePermission(entry.dependsOnPermission ?? 'location');
  const gatedDisabled = entry.dependsOnPermission
    ? dependency.status === 'denied'
    : false;

  switch (entry.type) {
    case 'control':
      return (
        <SettingControlRow
          entry={entry}
          isLast={isLast}
          gatedDisabled={gatedDisabled}
        />
      );
    case 'custom':
      return (
        <SettingCustomRow
          entry={entry}
          isLast={isLast}
          gatedDisabled={gatedDisabled}
        />
      );
    case 'permission':
      return <SettingPermissionRow entry={entry} isLast={isLast} />;
    default:
      return null;
  }
}
